import { z } from 'zod';
import type { SchedulingTypes } from '@/features/settings/custom-item-status/components/types';
import { booleanFilter, datetimeFilter, picklistFilter } from './common/filters';
import {
  type CustomFieldPicklistOperator,
  type DateOperator,
  type NumericOperator,
  type PicklistOperator,
  type StringOperator,
  stringOperators,
  type TextCustomFieldOperator,
} from './common/operators';

export type ItemFilterOperator =
  | DateOperator
  | PicklistOperator
  | StringOperator
  | CustomFieldPicklistOperator
  | TextCustomFieldOperator
  | NumericOperator;

const schedulingTypes: SchedulingTypes[] = ['DONE', 'IN_PROGRESS', 'TO_DO'];

export const datetimeItemFilterSchema = z.object({
  createdAt: datetimeFilter,
  forecastedFinishDateTime: datetimeFilter,
  forecastedStartDateTime: datetimeFilter,
  latestFinishDateTime: datetimeFilter,
  doneDateTime: datetimeFilter,
  plannedStartDateTime: datetimeFilter,
  plannedFinishDateTime: datetimeFilter,
  deadlineDateTime: datetimeFilter,
  earliestStartDateTime: datetimeFilter,
  updatedAt: datetimeFilter,
});

export const picklistItemFilterSchema = z.object({
  effortEstimate: picklistFilter,
  estimationId: picklistFilter,
  itemTypeId: picklistFilter,
  parentId: picklistFilter,
  ancestorId: picklistFilter,
  id: picklistFilter,
  loopId: picklistFilter,
  statusId: picklistFilter,
  teamId: picklistFilter,
  objectiveId: picklistFilter,
});

export const stringItemFilterSchema = z.object({
  'descendant.statusItemStatusType': z
    .object({
      value: z.array(z.enum(schedulingTypes)).nullable().optional(),
      operator: stringOperators,
    })
    .optional(),
  name: z
    .object({
      value: z.string().optional(),
      operator: stringOperators,
    })
    .optional(),
  externalKey: z
    .object({
      value: z.string().optional(),
      operator: stringOperators,
    })
    .optional(),
  quickFilters: z
    .object({
      value: z.array(z.string()).optional(),
      operator: stringOperators,
    })
    .optional(),
});

export const itemBooleanFilterSchema = z.object({
  isUnparented: booleanFilter,
  isMLImputed: booleanFilter,
  needsTeamOrEstimate: booleanFilter,
  inDoneSubtree: booleanFilter,
  unresolved: booleanFilter,
  hasObjective: booleanFilter,
  hasDependencies: booleanFilter,
  hasPredecessor: booleanFilter,
  hasSuccessor: booleanFilter,
  hasEstimate: booleanFilter,
  noEstimate: booleanFilter,
  hasProgress: booleanFilter,
  noProgress: booleanFilter,
});

export const isBooleanItemFilterKey = (
  key: string,
): key is keyof typeof itemBooleanFilterSchema.shape => {
  return Object.keys(itemBooleanFilterSchema.shape).includes(key);
};

export const itemFilterSearchSchema = z.object({
  ...datetimeItemFilterSchema.shape,
  ...picklistItemFilterSchema.shape,
  ...stringItemFilterSchema.shape,
  ...itemBooleanFilterSchema.shape,
});

export const isDateTimeFilterKey = (
  key: string,
): key is keyof typeof datetimeItemFilterSchema.shape => {
  return Object.keys(datetimeItemFilterSchema.shape).includes(key);
};

export const isPicklistFilterKey = (
  key: string,
): key is keyof typeof picklistItemFilterSchema.shape => {
  return Object.keys(picklistItemFilterSchema.shape).includes(key);
};

export const isStringFilterKey = (
  key: string,
): key is keyof typeof stringItemFilterSchema.shape => {
  return Object.keys(stringItemFilterSchema.shape).includes(key);
};
